import { PropsWithChildren } from 'react';

import { useAuthentication, UserGroups } from '@ravago/shared/authentication';
import { UserToggles } from '@ravago/shared/page-data/models/config';
import { Alert } from '@ravago/shared/radiance/components/alert/components/Alert/Alert';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BoxItem } from '@ravago/shared/radiance/components/box/components/BoxItem/BoxItem';
import { Icon } from '@ravago/shared/radiance/components/icon/components/Icon/Icon';
import { Link } from '@ravago/shared/radiance/components/link/components/Link/Link';
import { Sublevel } from '@ravago/shared/radiance/components/sublevel/components/Sublevel/Sublevel';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useTranslations } from '@ravago/shared/translations/client';

import { usePathname, useSearchParams } from 'next/navigation';

import { LoginButton } from '../../../shared/login-button/login-button';

interface Props extends PropsWithChildren {
   productNotFound: boolean;
   userToggles: UserToggles;
   leadTimeEnabled: boolean;
}

export const DynamicProductBlockContentGuard = ({ productNotFound, userToggles, leadTimeEnabled, children }: Props) => {
   const { user, login, canAuthenticate } = useAuthentication();
   const translate = useTranslations();

   const searchParams = useSearchParams();
   const pathname = usePathname();

   const handleLogin = () => login(`${pathname}?${searchParams.toString()}`);

   if (productNotFound) {
      return <Alert variant="error" headline={translate('request-for-quotation.product-not-available')} />;
   }

   if (!user && !userToggles.hasAnonymousToggle) {
      return (
         <Typography component="b" variant="body">
            {translate('request-for-quotation.anonymous-user')}
         </Typography>
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformPendingVerification) && !userToggles.hasPendingToggle) {
      return (
         <Typography component="b" variant="body">
            {translate('request-for-quotation.pending-user')}
         </Typography>
      );
   }

   if (user?.groups?.includes(UserGroups.digitalPlatformGuest) && !userToggles.hasGuestToggle) {
      return (
         <Typography component="b" variant="body">
            {translate('request-for-quotation.guest-user')}
         </Typography>
      );
   }

   if (!user && userToggles.hasAnonymousToggle && canAuthenticate) {
      return (
         <Box direction="column">
            {!leadTimeEnabled && (
               <Sublevel>
                  <Box direction="column" gap="xs">
                     <Typography component="span" variant="body">
                        {translate('request-for-quotation.login-message')}
                     </Typography>
                     <Box>
                        { }
                        <Link onClick={handleLogin} color="dark">
                           {translate('request-for-quotation.login')}
                        </Link>
                     </Box>
                  </Box>
               </Sublevel>
            )}
            {children}
            {leadTimeEnabled && (
               <Box direction="column">
                  <Sublevel>
                     <Box items="center">
                        <Icon size="heading-4" color="primary" icon="truck" />
                        <Box>
                           <Typography whiteSpace="normal" component="span" variant="body">
                              {translate('request-for-quotation.lead-time-login-message')}
                           </Typography>
                        </Box>
                        <BoxItem>
                           <LoginButton buttonLabel={translate('request-for-quotation.login')} variant="tertiary" />
                        </BoxItem>
                     </Box>
                  </Sublevel>
               </Box>
            )}
         </Box>
      );
   }

   return children;
};
